import { Checkbox, Tooltip } from '@mui/material';
import clsx from 'clsx';
import { BaseRow } from 'components/Table/types';
import { useCallback, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import Column from '../Column';
import SelectionContext from '../SelectionContext';
import useSelectionColumnStyles from './styles';
import { SelectionColumnProps } from './types';

function SelectionColumn<TRow extends BaseRow>(props: SelectionColumnProps<TRow>) {
  const { order = -999, className, disabled: disabledFn, disabledSelectAll } = props;
  const { t } = useTranslation('common');

  const classes = useSelectionColumnStyles(props);

  const { isAllSelected, selectAll, selectRow, selection, deselectAll, deselectRow } =
    useContext(SelectionContext);

  const defaultRender = useCallback(
    (_value, row: TRow) => {
      const { id } = row;
      const checked = selection?.includes(id);
      const label = { inputProps: { 'aria-label': t('selectRow', { id: id }) } };
      const disabled = disabledFn ? disabledFn(row) : false;
      return (
        <Checkbox
          disabled={disabled}
          checked={checked}
          onClick={(e) => e.stopPropagation()}
          onChange={() => {
            checked ? deselectRow(id) : selectRow(id);
          }}
          {...label}
        />
      );
    },
    [deselectRow, disabledFn, selectRow, selection, t]
  );

  const title = useMemo(() => {
    const label = { inputProps: { 'aria-label': t('allRowSelection') } };
    return (
      <Tooltip
        title={t('selectedNumber', {
          count: selection?.length ?? 0,
        })}
        placement='right'
      >
        <Checkbox
          key='selectAll'
          disabled={disabledSelectAll}
          checked={isAllSelected}
          indeterminate={!!selection?.length && !isAllSelected}
          onChange={() => {
            isAllSelected ? deselectAll() : selectAll();
          }}
          {...label}
        />
      </Tooltip>
    );
  }, [deselectAll, disabledSelectAll, isAllSelected, selectAll, selection?.length, t]);

  const renderSelectionColumn = () => {
    return (
      <Column<TRow>
        render={defaultRender}
        name='id'
        order={order}
        titleNode={title}
        noControl
        noSort
        {...props}
        className={clsx(className, classes.selectionCell)}
      />
    );
  };

  return renderSelectionColumn();
}

export default SelectionColumn;
